// Port of data_centrifuge_view.coffee#renderDataCentrifugePanel (:42-235), MINUS
// the trailing `page.sidebar.renderDataCentrifugeProps(true)` call — the controller's
// commit() owns the sidebar repaint so the write-then-repaint stays atomic.
//
// calcListProps mutates the passed-in listProps object IN PLACE for the diffed
// fields (vectors, quarantineUIDs, quarantineUIDsFiltered) and returns the same
// reference, exactly like the coffee. The _.isEqual guards keep nested array refs
// stable so the sidebar panel's memoized subtrees don't thrash on every poll tick.

import { isEqual } from 'lodash'

import {
  VECTORS_RAW,
  VECTORS_GROUPS,
  VECTORS_NAMES,
  getVectorIcon,
  getVectorColor,
  getParentGroup,
  toPrimaryVectorName
} from '@aytm/app/stats/page/data-centrifuge/lib/vectors'
import { can, CAPABILITY } from '@aytm/app/stats/page/data-quality-report/lib/tiers'
import {
  isAutoEnabledLikeVector,
  isAutoEnabledLikeSubVector,
  isDcQuarantineEnabled
} from '@aytm/app/stats/page/data-quality-report/controller/vector-predicates'

// The panel root DOM node doesn't change between renders — cache it once.
let statisticsRootEl = null

export const calcListProps = (listProps, state) => {
  const { tier, data, jobData, componentData, disabledVectors, filter, suspicionLevel, features, surveyConfig } = state

  if (!statisticsRootEl) statisticsRootEl = document.querySelector('#statistics-contents')
  listProps.root = statisticsRootEl

  // Defer setting vectors until survey features are loaded so exclude_vectors is applied.
  if (!surveyConfig) return listProps

  const statuses = componentData.dcStatuses || {}

  // Hoisted O(1) lookups + filter helper.
  const quarantineLookup = {}
  componentData.quarantineUIDs.forEach(uid => {
    quarantineLookup[uid] = true
  })

  const disabledVectorsLookup = {}
  disabledVectors.forEach(id => {
    disabledVectorsLookup[id] = true
  })

  const scoresByUid = {}
  componentData.scores.forEach(s => {
    scoresByUid[s[0]] = s
  })

  const filterMatch = uid => {
    const isQuarantined = quarantineLookup[uid]
    return filter === 'all' || (!isQuarantined && filter !== 'quarantined') || (isQuarantined && filter !== 'flagged')
  }

  const vectorIds = componentData.vectorIds

  // Below the forensic tiers the per-respondent rows are withheld outright, so the score walk
  // above counts nothing and every row would read zero. The payload carries a per-vector
  // aggregate for exactly that case; it is keyed by display name, which is the vocabulary both
  // the parent rows and the group children already carry. Read only when there is no row set to
  // walk, so a tier that has one keeps deriving its own figures and stays live under selection.
  const breakdown = componentData.scores.length === 0 ? data?.vectorBreakdown : null
  const breakdownTotal = name => (breakdown && breakdown[name]) || 0

  const vectorIdsIndex = {}
  vectorIds.forEach((id, i) => {
    vectorIdsIndex[id] = i
  })

  // Single walk over componentData.scores: buckets each suspicious + filter-passing
  // score into every non-disabled contributing vector, accumulates the panel total,
  // and records which UIDs contributed.
  let panelTotal = 0
  let advisoryPanelTotal = 0

  const vectorScoresByVectorId = {}
  const contributingUidsLookup = {}

  componentData.scores.forEach(s => {
    const uid = s[0]
    if (suspicionLevel > componentData.suspicionScores[uid]) return
    if (!filterMatch(uid)) return

    let contributed = false
    vectorIds.forEach((vectorId, vi) => {
      if (disabledVectorsLookup[vectorId]) return
      if (!(s[vi + 1] > 0)) return
      contributed = true
      ;(vectorScoresByVectorId[vectorId] ||= []).push(s)
    })

    if (contributed) {
      panelTotal += 1
      contributingUidsLookup[uid] = true
    }
  })

  // Advisory responses never enter componentData.scores, so they need their own walk. A vector
  // only counts them while it is checked — unchecked is the first-load default for these ids.
  const advisoryLeafCounts = {}
  const advisoryParentCounts = {}
  const advisoryProjectedUIDs = {}

  const advisoryUIDs = can(tier, CAPABILITY.SHOW_ADVISORY) ? componentData.advisoryUIDs : []
  advisoryUIDs.forEach(uid => {
    if (!filterMatch(uid)) return

    const seenParents = {}
    let contributed = false
    Object.keys(componentData.advisoryScores[uid] || {}).forEach(leafId => {
      const parentId = getParentGroup(leafId) || leafId
      // Re-enabling a parent restores its children with it, so the projected tally below reads
      // the Advisory hit whatever the disabled flags under that parent say.
      ;(advisoryProjectedUIDs[parentId] ||= new Set()).add(uid)
      if (disabledVectorsLookup[leafId] || disabledVectorsLookup[parentId]) return

      contributed = true
      advisoryLeafCounts[leafId] = (advisoryLeafCounts[leafId] || 0) + 1
      if (!seenParents[parentId]) {
        seenParents[parentId] = true
        advisoryParentCounts[parentId] = (advisoryParentCounts[parentId] || 0) + 1
      }
    })

    if (contributed) advisoryPanelTotal += 1
  })

  // Both tiers render in static-taxonomy order — never componentData.groups key order,
  // which follows whichever child a response happened to carry first.
  const groupChildren = {}
  VECTORS_GROUPS.forEach(g => {
    groupChildren[g.id] = g.vectors
  })

  // The run's own list of the primaries it flagged. It carries no figures, so it decides two things
  // only: which rows the least capable tier renders, and whether a row with no figure behind it
  // reads as one that fired. The rollup into primaries is the producer's, and it is not always
  // applied — a name that arrives at sub-vector level is rolled up here so it names the row that
  // owns it rather than matching nothing and reading as a vector the run never mentioned.
  const namedPrimaries = new Set((data?.primaryVectors || []).map(toPrimaryVectorName))
  const namedPrimary = name => namedPrimaries.has(name)

  // That tier is shown the run's findings rather than the whole screening taxonomy, so its rows are
  // the primaries the payload names. An absent list is not an empty run: until the report arrives
  // there is nothing to name, and seeding the taxonomy would show vectors it has not spoken about.
  const rowIds = can(tier, CAPABILITY.SHOW_SUBVECTORS)
    ? VECTORS_RAW
    : VECTORS_RAW.filter(id => namedPrimary(VECTORS_NAMES[id]))

  const vectors = rowIds.map(id => {
    // Vectors that ran successfully but produced no scores keep an enabled visual.
    const autoEnabledLike = isAutoEnabledLikeVector(componentData, id)

    const vector = {
      id,
      // NOTE: legacy coffee had a substitutions branch here, but calcData never
      // produces componentData.substitutions, so it always fell through to the name.
      text: VECTORS_NAMES[id],
      icon: getVectorIcon(id)
    }

    let vectorScores = []

    // Disabled vectors surface the projected count (what re-enabling would catch).
    if (disabledVectorsLookup[id]) {
      vectorScores = Object.hasOwn(componentData.disabledVectorsScores, id)
        ? componentData.disabledVectorsScores[id].filter(s => suspicionLevel <= s[1] && filterMatch(s[0]))
        : []
      const projectedUIDs = new Set(vectorScores.map(score => score[0]))
      advisoryProjectedUIDs[id]?.forEach(uid => projectedUIDs.add(uid))
      if (projectedUIDs.size > 0) vector.actualTotal = projectedUIDs.size

      vector.total = 0
      vector.color = '#999999'
      vector.checked = false
      vector.disabled = jobData.vectors?.[id] !== undefined
    } else {
      const index = vectorIdsIndex[id]
      if (index === undefined) {
        // With no row set every id lands here, so this is also the branch the aggregate answers.
        const aggregate = breakdownTotal(VECTORS_NAMES[id])
        // The rows below the forensic tiers are rebuilt to the client-visible vectors, so a vector
        // outside that whitelist can fire and appear in neither the walk nor the aggregate. The
        // run's own list of flagged primaries is the only thing that still says so.
        const fired = aggregate > 0 || namedPrimary(VECTORS_NAMES[id])

        vector.total = aggregate
        // A row known to have fired reads as one that fired, exactly as it would where the scores
        // were walked — greying it would state the vector found nothing.
        vector.color = fired ? getVectorColor(id) : '#999999'
        vector.checked = fired || autoEnabledLike
        vector.disabled = jobData.vectors?.[id] !== undefined || (!fired && !autoEnabledLike)
      } else {
        vectorScores = vectorScoresByVectorId[id] || []

        vector.total = vectorScores.length
        vector.color = getVectorColor(id)
        vector.checked = true
        vector.disabled = jobData.vectors?.[id] !== undefined
      }
    }

    const advisoryTotal = advisoryParentCounts[id] || 0
    if (advisoryTotal > 0) {
      vector.total += advisoryTotal
      vector.color = getVectorColor(id)
      vector.checked = true
      // A row carrying Advisory hits has real data behind it, so it stays interactive even on an
      // Advisory-only run, where every vector is otherwise seeded idle.
      vector.disabled = false
    }

    if (Object.hasOwn(componentData.groups, id)) {
      // Build the children list unconditionally for every parent that owns one.
      const childIds = (groupChildren[id] || []).filter(key => Object.hasOwn(componentData.groups[id], key))
      vector.group = childIds.map(key => {
        const item = componentData.groups[id][key]
        const autoEnabledLikeSub = isAutoEnabledLikeSubVector(componentData, key, item)

        // No projected count on the child shape: the "what re-enabling would catch" figure is a
        // parent-row affordance only. (The legacy coffee computed one here, off an undefined var.)
        const subvector = { id: key, text: item.text, icon: getVectorIcon(key), item }

        // `item.total` is the seeded skeleton's own count, which is zero wherever the aggregate is
        // what carries the figures — so both the displayed total and the enabled state read from
        // whichever source is live for this tier.
        const itemTotal = breakdown ? breakdownTotal(item.text) : item.total
        const total = breakdown ? itemTotal : vectorScores.filter(s => item.scores.includes(s[0])).length

        if (disabledVectorsLookup[id] || disabledVectorsLookup[key]) {
          subvector.total = 0
          subvector.checked = false
        } else {
          subvector.total = total
          subvector.checked = itemTotal > 0 || autoEnabledLikeSub
        }

        subvector.disabled = !itemTotal && !autoEnabledLikeSub

        const advisoryChildTotal = advisoryLeafCounts[key] || 0
        if (advisoryChildTotal > 0) {
          subvector.total += advisoryChildTotal
          subvector.checked = true
          subvector.disabled = false
        }

        return subvector
      })
    }

    return vector
  })

  if (!isEqual(vectors, listProps.vectors)) listProps.vectors = vectors

  listProps.total = panelTotal + advisoryPanelTotal

  listProps.statuses = statuses

  listProps.scoresUIDs = componentData.scoresUIDs

  const restoringUIDs = componentData.restoringUIDs || []
  if (!isEqual(restoringUIDs, listProps.restoringUIDs)) listProps.restoringUIDs = restoringUIDs

  if (!isEqual(componentData.quarantineUIDs, listProps.quarantineUIDs)) {
    listProps.quarantineUIDs = componentData.quarantineUIDs
  }

  const quarantineUIDsFiltered = componentData.quarantineUIDs.filter(uid => {
    const s = scoresByUid[uid]
    if (!s) return false

    return (
      ['all', 'quarantined'].includes(filter) &&
      suspicionLevel <= componentData.suspicionScores[s[0]] &&
      contributingUidsLookup[uid]
    )
  })
  if (!isEqual(quarantineUIDsFiltered, listProps.quarantineUIDsFiltered)) {
    listProps.quarantineUIDsFiltered = quarantineUIDsFiltered
  }

  listProps.dcLastRunTs = componentData.dcLastRunTs || null
  listProps.dcAnalyzedCount = componentData.dcAnalyzedCount || 0
  listProps.notAnalyzedCount = componentData.notAnalyzedCount || 0

  listProps.dcQuarantineEnabled = isDcQuarantineEnabled(componentData, data, features, surveyConfig)

  return listProps
}
